<template>
  <v-sheet outlined rounded color="grey lighten-5" class="px-4 py-3">
    <div class="d-flex align-center mb-3">
      <v-icon color="primary" class="mr-2">mdi-crosshairs-gps</v-icon>
      <span class="text-subtitle-1">{{ $t('tractDetails') }}</span>
    </div>

    <div class="d-flex flex-wrap align-start" style="gap: 12px 40px;">
      <div v-for="fact in harvestFacts" :key="fact.key">
        <div class="dd-eyebrow">{{ fact.label }}</div>
        <div class="ddt-fact-value text-body-2 mt-1 d-flex align-center" :class="{ 'grey--text': !fact.value }">{{ fact.value || $t('notSpecified') }}</div>
      </div>
      <div v-if="hasConversionField">
        <div class="dd-eyebrow mb-1">{{ $t('conversion') }}</div>
        <ConversionImpactChip :impact="conversionImpact" :text="conversionName"/>
      </div>
    </div>

    <v-divider class="my-3"/>

    <div class="dd-eyebrow mb-1">{{ $t('certifications') }}</div>
    <div v-if="certifications.length === 0" class="text-body-2 grey--text">{{ $t('none') }}</div>
    <div
    v-for="certification in certifications"
    :key="certification.certificationId"
    class="d-flex align-center flex-wrap text-body-2"
    style="gap: 0 8px;">
      <span class="font-weight-medium">{{ certification.name }}</span>
      <span v-if="certification.basisAccountId" class="text-caption grey--text text--darken-1 d-inline-flex align-center">
        <Icon
        v-if="isBasisExpired(certification)"
        icon="mdi-alert"
        iconColor="warning"
        :tooltipText="$t('basisExpired')"/>
        {{ basisCertificationText(certification) }}
      </span>
    </div>

    <div class="dd-eyebrow mt-3 mb-1">{{ $t('accounts') }}</div>
    <div class="d-flex flex-wrap" style="gap: 8px 32px;">
      <div v-for="account in accounts" :key="account.key">
        <AccountName
        v-if="account.accountId"
        class="ddt-account text-body-2"
        :accountId="account.accountId"
        :certificationStatus="account.certificationStatus">
          {{ account.name }}
        </AccountName>
        <div v-else class="text-body-2 grey--text">{{ $t('notSpecified') }}</div>
        <div class="text-caption grey--text text--darken-1">{{ account.role }}</div>
      </div>
    </div>
  </v-sheet>
</template>

<script>
import { formatPercentage } from '@/utils/NumericMutations.js'
import { isBasisExpired, basisCertificationText } from '@/utils/AccountCertifications.js'
import { hasConversionField } from '@/utils/dueDiligence/presentation.js'

export default {
  name: 'DueDiligenceTractDetails',

  components: {
    AccountName: () => import('@/components/account/AccountName.vue'),
    ConversionImpactChip: () => import('@/components/due-diligence/ConversionImpactChip.vue'),
    Icon: () => import('@/components/helper/Icon.vue')
  },

  props: {
    queueItem: {
      type: Object,
      required: true
    }
  },

  computed: {
    harvestFacts () {
      return [
        { key: 'harvestType', label: this.$t('harvestType'), value: this.queueItem.harvestTypeName },
        { key: 'loggingMethod', label: this.$t('loggingMethod'), value: this.queueItem.loggingMethodName }
      ]
    },

    hasConversionField () {
      return hasConversionField(this.queueItem)
    },

    conversionImpact () {
      return this.queueItem.conversion?.impact ?? null
    },

    conversionName () {
      return this.queueItem.conversion?.name ?? null
    },

    certifications () {
      return [...(this.queueItem.tractCertifications ?? [])].sort((a, b) => a.name.localeCompare(b.name))
    },

    landowners () {
      return [...(this.queueItem.landowners ?? [])].sort((a, b) => b.ownership - a.ownership || a.name.localeCompare(b.name))
    },

    accounts () {
      const landowners = this.landowners.length > 0
        ? this.landowners.map(l => ({ ...l, key: `landowner-${l.accountId}`, role: `${this.$t('landOwner')} · ${formatPercentage(l.ownership)}` }))
        : [{ key: 'landOwner', role: this.$t('landOwner') }]
      const related = [
        { key: 'supplier', role: this.$t('supplier'), account: this.queueItem.supplierAccount },
        { key: 'consultingForester', role: this.$t('consultingForester'), account: this.queueItem.consultingForesterAccount }
      ].filter(a => a.account).map(a => ({ ...a.account, key: a.key, role: a.role }))
      return [...landowners, ...related]
    }
  },

  methods: {
    isBasisExpired,
    basisCertificationText
  }
}
</script>

<style scoped>
/* Keeps the harvest values level with the conversion chip beside them */
.ddt-fact-value {
  min-height: 26px;
}
.ddt-account ::v-deep .v-icon {
  cursor: pointer;
}
</style>
